import { apiClient } from './client';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: {
    id: number;
    email: string;
    username: string;
  role: 'user' | 'partner_admin' | 'demo_admin' | 'admin' | 'root_admin';
    is_active: boolean;
    is_verified: boolean;
    is_adult_verified: boolean;
    credits: number;
    membership_type: 'free' | 'basic' | 'premium' | 'vip';
    membership_expires_at?: string | null;
    created_at: string;
    full_name?: string | null;
  };
}

export const authAPI = {
  login: (data: LoginRequest) => {
    const formData = new URLSearchParams();
    formData.append('username', data.email);
    formData.append('password', data.password);

    return apiClient.post<AuthResponse>('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  },
  register: (data: RegisterRequest, params?: { referred_by?: string; referral_source?: string }) => {
    const query = new URLSearchParams();
    if (params?.referred_by) query.set('referred_by', params.referred_by);
    if (params?.referral_source) query.set('referral_source', params.referral_source);
    const qs = query.toString();
    return apiClient.post<AuthResponse>(`/auth/register${qs ? `?${qs}` : ''}`, data);
  },
  getCurrentUser: () => apiClient.get('/auth/me'),
  // Cookie-basierter Refresh; zusätzlich Token im Body, falls Cookie nicht ankommt
  refresh: () => {
    // Versuche Refresh-Token aus Cookie zu lesen
    const match = /(?:^|; )refresh_token=([^;]+)/.exec(document.cookie);
    const tokenRaw = match?.[1] ? decodeURIComponent(match[1]) : undefined;
    const body = tokenRaw ? { refresh_token: tokenRaw } : {} as Record<string, unknown>;
    return apiClient.post<AuthResponse>('/auth/refresh', body);
  },
  // Logout via Backend, das Cookies löscht
  logout: () => apiClient.post('/auth/logout', {}),
  forgotPassword: (email: string) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, new_password: string) =>
    apiClient.post('/auth/reset-password', { token, new_password }),
  resendVerification: () => apiClient.post('/auth/resend-verification', {}),
};

// Keep the existing authService for backward compatibility
export const authService = {
  async login(data: LoginRequest): Promise<AuthResponse> {
    // Convert to form data for OAuth2PasswordRequestForm
    const formData = new URLSearchParams();
    formData.append('username', data.email); // Backend expects username field
    formData.append('password', data.password);
    
    const response = await apiClient.post<AuthResponse>('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    
    return response.data;
  },

  async register(data: RegisterRequest, params?: { referred_by?: string; referral_source?: string }): Promise<AuthResponse> {
    const query = new URLSearchParams();
    if (params?.referred_by) query.set('referred_by', params.referred_by);
    if (params?.referral_source) query.set('referral_source', params.referral_source);
    const qs = query.toString();
    const response = await apiClient.post<AuthResponse>(`/auth/register${qs ? `?${qs}` : ''}`, data);
    return response.data;
  },

  async getCurrentUser() {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  async refresh() {
    const response = await apiClient.post<AuthResponse>('/auth/refresh', {});
    return response.data;
  },

  logout() {
    // Request an Backend – Cookies werden dort gelöscht
    void apiClient.post('/auth/logout', {}).finally(() => {
      window.location.href = '/auth';
    });
  },

  isAuthenticated(): boolean {
    // Cookie-basierte Auth: Wenn /auth/me funktioniert, ist user gesetzt
    // Für schnelle Checks weiterhin boolean, aber ohne localStorage
    return true;
  },

  getAccessToken(): string | null {
    // Keine direkte Token-Ausgabe mehr
    return null;
  },
};
